import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingState } from '../../types';
import skillLevelService, { SkillLevel } from '../../services/skillLevelService';

interface SkillLevelState extends LoadingState {
  skillLevels: SkillLevel[];
  selectedSkillLevel: SkillLevel | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  searchQuery: string;
}

const initialState: SkillLevelState = {
  skillLevels: [],
  selectedSkillLevel: null,
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    pages: 0,
    hasMore: false,
  },
  searchQuery: '',
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchSkillLevels = createAsyncThunk(
  'skillLevels/fetchSkillLevels',
  async (params: { page?: number; limit?: number; append?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 5, append = false } = params;
      const response = await skillLevelService.getActiveSkillLevels({ page, limit });
      return { ...response, append };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch skill levels');
    }
  }
);

export const createSkillLevel = createAsyncThunk(
  'skillLevels/createSkillLevel',
  async (data: { name: string; status: 'active' | 'inactive' }, { rejectWithValue }) => {
    try {
      const response = await skillLevelService.createSkillLevel(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create skill level');
    }
  }
);

const skillLevelSlice = createSlice({
  name: 'skillLevels',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedSkillLevel: (state, action: PayloadAction<SkillLevel | null>) => {
      state.selectedSkillLevel = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.skillLevels = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Skill Levels
      .addCase(fetchSkillLevels.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSkillLevels.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, append } = action.payload;
        
        if (append) {
          state.skillLevels.push(...data.data as any);
        } else {
          state.skillLevels = data.data as any;
        }
        
        state.pagination = {
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          pages: data.pagination.pages || data.pagination.totalPages,
          hasMore: data.pagination.page < (data.pagination.pages || data.pagination.totalPages),
        };
      })
      .addCase(fetchSkillLevels.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Skill Level
      .addCase(createSkillLevel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSkillLevel.fulfilled, (state, action) => {
        state.isLoading = false;
        const newSkill = action.payload;
        
        // Create a properly typed object for WritableDraft
        const skillToAdd: SkillLevel = {
          _id: newSkill._id,
          name: newSkill.name,
          slug: newSkill.slug,
          status: newSkill.status,
          isDeleted: newSkill.isDeleted,
          createdBy: newSkill.createdBy,
          createdAt: newSkill.createdAt,
          updatedAt: newSkill.updatedAt
        };
        
        state.skillLevels.unshift(skillToAdd as any);
        state.pagination.total += 1;
      })
      .addCase(createSkillLevel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedSkillLevel,
  setSearchQuery,
  resetPagination,
} = skillLevelSlice.actions;

export default skillLevelSlice.reducer;
